import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Calendar } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import type { Locale } from 'antd/es/locale';
import enUS from 'antd/es/locale/en_US';
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from '../icons';
import './CalendarOverlay.css';

// Custom locale with single-letter weekday headers
const customLocale: Locale = {
  ...enUS,
  Calendar: {
    ...enUS.Calendar,
    lang: {
      ...enUS.Calendar?.lang,
      locale: 'en',
      shortWeekDays: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
    },
  },
};

export interface CalendarOverlayProps {
  /** Whether the calendar overlay is visible */
  open: boolean;
  /** Callback when the overlay is closed */
  onClose: () => void;
  /** Currently selected date */
  value?: Dayjs;
  /** Callback when a date is selected */
  onSelect?: (date: Dayjs) => void;
  /** Minimum selectable date */
  minDate?: Dayjs;
  /** Maximum selectable date */
  maxDate?: Dayjs;
  /** Array of date strings (YYYY-MM-DD) that have shows */
  datesWithShows?: string[];
}

export default function CalendarOverlay({
  open,
  onClose,
  value,
  onSelect,
  minDate,
  maxDate,
  datesWithShows = [],
}: CalendarOverlayProps) {
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(open);
  const [currentValue, setCurrentValue] = useState<Dayjs>(value || dayjs());

  useEffect(() => {
    if (open) {
      setShouldRender(true);
      setIsClosing(false);
      // Update current value when opening
      if (value) {
        setCurrentValue(value);
      }
    } else if (shouldRender) {
      setIsClosing(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [open, shouldRender, value]);

  if (!shouldRender) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleDateSelect = (date: Dayjs, info: { source: 'year' | 'month' | 'date' | 'customize' }) => {
    if (info.source === 'date') {
      setCurrentValue(date);
      onSelect?.(date);
      onClose();
    }
  };

  const handlePanelChange = (date: Dayjs) => {
    setCurrentValue(date);
  };

  const handleTodayClick = () => {
    const today = dayjs();
    setCurrentValue(today);
  };

  const disabledDate = (current: Dayjs) => {
    if (minDate && current.isBefore(minDate, 'day')) {
      return true;
    }
    if (maxDate && current.isAfter(maxDate, 'day')) {
      return true;
    }
    return false;
  };

  // Custom cell render to show single digit days (1-9 instead of 01-09)
  const fullCellRender = (current: Dayjs, info: { originNode: React.ReactElement; today: Dayjs; type: string }) => {
    if (info.type !== 'date') {
      return info.originNode;
    }

    const day = current.date();
    const isSelected = current.isSame(currentValue, 'day');
    const isToday = current.isSame(dayjs(), 'day');
    const isCurrentMonth = current.month() === currentValue.month();
    const isDisabled = disabledDate(current);
    const hasShow = datesWithShows.includes(current.format('YYYY-MM-DD'));

    return (
      <div
        className={`calendar-overlay__cell ${isSelected ? 'calendar-overlay__cell--selected' : ''} ${isToday ? 'calendar-overlay__cell--today' : ''} ${!isCurrentMonth ? 'calendar-overlay__cell--outside' : ''} ${isDisabled ? 'calendar-overlay__cell--disabled' : ''} ${hasShow ? 'calendar-overlay__cell--has-show' : ''}`}
      >
        {day}
        {hasShow && <span className="calendar-overlay__show-dot" />}
      </div>
    );
  };

  const customHeader = ({
    value: headerValue,
    onChange,
  }: {
    value: Dayjs;
    type: 'year' | 'month';
    onChange: (date: Dayjs) => void;
    onTypeChange: (type: 'year' | 'month') => void;
  }) => {
    const monthYear = headerValue.format('MMMM YYYY');

    const goToPrevMonth = () => {
      onChange(headerValue.subtract(1, 'month'));
    };

    const goToNextMonth = () => {
      onChange(headerValue.add(1, 'month'));
    };

    return (
      <div className="calendar-overlay__header">
        <button
          className="calendar-overlay__nav-btn"
          onClick={goToPrevMonth}
          aria-label="Previous month"
        >
          <ChevronLeftIcon size="m" tone="darkGrey" />
        </button>
        <span className="calendar-overlay__month-year">{monthYear}</span>
        <button
          className="calendar-overlay__nav-btn"
          onClick={goToNextMonth}
          aria-label="Next month"
        >
          <ChevronRightIcon size="m" tone="darkGrey" />
        </button>
      </div>
    );
  };

  const content = (
    <div
      className={`calendar-overlay__backdrop ${isClosing ? 'calendar-overlay--closing' : ''}`}
      onClick={handleBackdropClick}
    >
      <div className={`calendar-overlay__container ${isClosing ? 'calendar-overlay--closing' : ''}`}>
        <Calendar
          fullscreen={false}
          value={currentValue}
          onSelect={handleDateSelect}
          onPanelChange={handlePanelChange}
          headerRender={customHeader}
          fullCellRender={fullCellRender}
          disabledDate={disabledDate}
          locale={customLocale.Calendar?.lang}
        />

        {/* Today button */}
        <button
          className="calendar-overlay__today-btn"
          onClick={handleTodayClick}
        >
          Today
        </button>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
